import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  deleteObject,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { db, storage } from "../../../firebase";
import {
  FaSearch,
  FaEdit,
  FaTrashAlt,
  FaEye,
  FaEyeSlash,
  FaCircleNotch,
  FaBoxOpen,
  FaTimes,
  FaCamera,
  FaSave,
  FaCheckSquare,
} from "react-icons/fa";
import "./allProducts.scss";

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filters & Bulk Select
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [visibilityFilter, setVisibilityFilter] = useState("All");
  const [selectedProducts, setSelectedProducts] = useState([]);

  // Edit Modal States
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const fileInputRef = useRef(null);

  // Background Scroll Lock for Modal
  useEffect(() => {
    if (editModalOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [editModalOpen]);

  // Fetch Products & Apply ERP Logic
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const q = query(
          collection(db, "products"),
          orderBy("createdAt", "desc"),
        );
        const snapshot = await getDocs(q);

        const now = Date.now();
        const TWO_DAYS_MS = 48 * 60 * 60 * 1000;
        let prodData = [];

        for (const document of snapshot.docs) {
          let data = { id: document.id, ...document.data() };

          // ADVANCED ERP LOGIC: 48-Hour Auto Revert Sold Out -> Catalog
          if (data.stockStatus === "Sold Out" && data.soldOutAt) {
            const soldTime = data.soldOutAt.toMillis
              ? data.soldOutAt.toMillis()
              : data.soldOutAt;
            if (now - soldTime >= TWO_DAYS_MS) {
              await updateDoc(doc(db, "products", data.id), {
                stockStatus: "Made to Order",
                soldOutAt: null,
              });
              data.stockStatus = "Made to Order";
              data.soldOutAt = null;
            }
          }
          prodData.push(data);
        }
        setProducts(prodData);
      } catch (error) {
        console.error("Error fetching products: ", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Filter Logic
  useEffect(() => {
    let temp = [...products];
    if (statusFilter !== "All")
      temp = temp.filter((p) => p.stockStatus === statusFilter);
    if (visibilityFilter !== "All")
      temp = temp.filter((p) => p.visibility === visibilityFilter);
    if (search) {
      const s = search.toLowerCase();
      temp = temp.filter(
        (p) => p.searchIndex?.includes(s) || p.sku?.toLowerCase().includes(s),
      );
    }
    setFilteredProducts(temp);
  }, [statusFilter, visibilityFilter, search, products]);

  // ==========================================
  // INLINE & BULK ACTIONS
  // ==========================================
  const handleSelectAll = (e) => {
    if (e.target.checked)
      setSelectedProducts(filteredProducts.map((p) => p.id));
    else setSelectedProducts([]);
  };

  const handleSelectOne = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  // Instant Inline Status Change
  const handleInlineStatusChange = async (product, newStatus) => {
    try {
      const updates = { stockStatus: newStatus };
      if (newStatus === "Sold Out" && product.stockStatus !== "Sold Out")
        updates.soldOutAt = serverTimestamp();
      else if (newStatus !== "Sold Out") updates.soldOutAt = null;

      await updateDoc(doc(db, "products", product.id), updates);
      setProducts(
        products.map((p) => (p.id === product.id ? { ...p, ...updates } : p)),
      );
    } catch (e) {
      alert("Failed to update status.");
    }
  };

  // Instant Inline Visibility Change
  const handleToggleVisibility = async (product) => {
    const newVisibility = product.visibility === "Public" ? "Hidden" : "Public";
    try {
      await updateDoc(doc(db, "products", product.id), {
        visibility: newVisibility,
      });
      setProducts(
        products.map((p) =>
          p.id === product.id ? { ...p, visibility: newVisibility } : p,
        ),
      );
    } catch (e) {
      alert("Failed to update visibility.");
    }
  };

  // Bulk Command Center Execution
  const handleBulkAction = async (action, value) => {
    if (
      !window.confirm(`Apply this change to ${selectedProducts.length} items?`)
    )
      return;
    setIsBulkUpdating(true);

    try {
      if (action === "delete") {
        await Promise.all(
          selectedProducts.map(async (id) => {
            const product = products.find((p) => p.id === id);
            if (product?.images?.length > 0) {
              await Promise.all(
                product.images.map(async (url) => {
                  try {
                    const path = decodeURIComponent(
                      url.split("/o/")[1].split("?")[0],
                    );
                    await deleteObject(ref(storage, path));
                  } catch (err) {}
                }),
              );
            }
            await deleteDoc(doc(db, "products", id));
          }),
        );
        setProducts(products.filter((p) => !selectedProducts.includes(p.id)));
      } else {
        const updates = {};
        if (action === "visibility") updates.visibility = value;
        if (action === "stockStatus") {
          updates.stockStatus = value;
          updates.soldOutAt = value === "Sold Out" ? serverTimestamp() : null;
        }

        await Promise.all(
          selectedProducts.map((id) =>
            updateDoc(doc(db, "products", id), updates),
          ),
        );
        setProducts(
          products.map((p) =>
            selectedProducts.includes(p.id) ? { ...p, ...updates } : p,
          ),
        );
      }
      setSelectedProducts([]);
    } catch (e) {
      alert("Bulk action failed.");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleDeleteSingle = async (product) => {
    if (!window.confirm(`Permanently delete ${product.sku}?`)) return;
    try {
      if (product.images?.length > 0) {
        await Promise.all(
          product.images.map(async (url) => {
            try {
              const path = decodeURIComponent(
                url.split("/o/")[1].split("?")[0],
              );
              await deleteObject(ref(storage, path));
            } catch (err) {}
          }),
        );
      }
      await deleteDoc(doc(db, "products", product.id));
      setProducts(products.filter((p) => p.id !== product.id));
      setSelectedProducts((prev) => prev.filter((id) => id !== product.id));
    } catch (error) {
      alert("Failed to delete product.");
    }
  };

  // EDIT MODAL LOGIC
  const openEditModal = (product) => {
    setEditForm({ ...product, newFiles: [] });
    setEditModalOpen(true);
  };
  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };
  const handleEditImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setEditForm((prev) => ({
      ...prev,
      newFiles: [...(prev.newFiles || []), ...files],
    }));
  };
  const handleRemoveExistingImage = (idx) => {
    setEditForm((prev) => {
      const updated = [...prev.images];
      updated.splice(idx, 1);
      return { ...prev, images: updated };
    });
  };
  const handleRemoveNewImage = (idx) => {
    setEditForm((prev) => {
      const updated = [...prev.newFiles];
      updated.splice(idx, 1);
      return { ...prev, newFiles: updated };
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      let finalImageUrls = [...(editForm.images || [])];
      if (editForm.newFiles?.length > 0) {
        const uploadedUrls = await Promise.all(
          editForm.newFiles.map(async (img) => {
            const storageRef = ref(
              storage,
              `products/erp_edit_${Date.now()}_${img.name}`,
            );
            await uploadBytes(storageRef, img);
            return await getDownloadURL(storageRef);
          }),
        );
        finalImageUrls = [...finalImageUrls, ...uploadedUrls];
      }

      const updates = {
        name: editForm.name,
        sku: editForm.sku,
        stockStatus: editForm.stockStatus,
        quantity:
          editForm.stockStatus === "In Stock" ? parseInt(editForm.quantity) : 0,
        weight: editForm.weight,
        size: editForm.size,
        carat: editForm.carat,
        visibility: editForm.visibility,
        images: finalImageUrls,
      };

      if (
        editForm.stockStatus === "Sold Out" &&
        editForm.stockStatus !==
          products.find((p) => p.id === editForm.id).stockStatus
      ) {
        updates.soldOutAt = serverTimestamp();
      }

      await updateDoc(doc(db, "products", editForm.id), updates);
      setProducts(
        products.map((p) => (p.id === editForm.id ? { ...p, ...updates } : p)),
      );
      setEditModalOpen(false);
      setEditForm(null);
    } catch (error) {
      console.error(error);
      alert("Failed to save product.");
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    if (status === "In Stock") return "success";
    if (status === "Sold Out") return "danger";
    return "warning";
  };

  return (
    <div className="v-adm-inv-wrapper fade-in">
      {/* HEADER & FILTERS */}
      <div className="v-adm-inv-header">
        <div>
          <h2>Master Inventory</h2>
          <p>Manage vault, visibility, and stock instantly.</p>
        </div>
      </div>

      <div className="v-adm-inv-toolbar">
        <div className="v-adm-inv-search-box">
          <FaSearch className="icon" />
          <input
            type="text"
            placeholder="Search SKU or Name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="v-adm-inv-filters">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Statuses</option>
            <option value="In Stock">In Stock Only</option>
            <option value="Made to Order">Made to Order</option>
            <option value="Sold Out">Sold Out</option>
          </select>
          <select
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value)}
          >
            <option value="All">All Visibility</option>
            <option value="Public">Public (Visible)</option>
            <option value="Hidden">Hidden</option>
          </select>
        </div>
      </div>

      {/* DATA AREA */}
      <div className="v-adm-inv-list-container">
        {isLoading ? (
          <div className="v-adm-inv-loader">
            <FaCircleNotch className="spin" /> Syncing Secure Vault...
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="v-adm-inv-empty">
            <FaBoxOpen /> <p>No products found matching criteria.</p>
          </div>
        ) : (
          <div className="v-adm-inv-table-responsive">
            <table className="v-adm-inv-table">
              <thead>
                <tr>
                  <th className="checkbox-col">
                    <div className="custom-checkbox">
                      <input
                        type="checkbox"
                        id="selectAll"
                        onChange={handleSelectAll}
                        checked={
                          selectedProducts.length === filteredProducts.length &&
                          filteredProducts.length > 0
                        }
                      />
                      <label htmlFor="selectAll"></label>
                    </div>
                  </th>
                  <th>Product Details</th>
                  <th>Inventory State (Click to Edit)</th>
                  <th>Visibility</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((p) => (
                  <tr
                    key={p.id}
                    className={`${p.visibility === "Hidden" ? "row-dimmed" : ""} ${selectedProducts.includes(p.id) ? "row-selected" : ""}`}
                  >
                    {/* CHECKBOX */}
                    <td className="checkbox-col" data-label="Select">
                      <div className="custom-checkbox">
                        <input
                          type="checkbox"
                          id={`cb-${p.id}`}
                          checked={selectedProducts.includes(p.id)}
                          onChange={() => handleSelectOne(p.id)}
                        />
                        <label htmlFor={`cb-${p.id}`}></label>
                      </div>
                    </td>

                    {/* PRODUCT INFO */}
                    <td data-label="Product">
                      <div className="v-adm-inv-td-product">
                        <div className="td-img">
                          {p.images?.[0] ? (
                            <img src={p.images[0]} alt={p.name} />
                          ) : (
                            <FaBoxOpen />
                          )}
                        </div>
                        <div className="td-info">
                          <span className="td-name">{p.name}</span>
                          <span className="td-sku">
                            {p.sku} • {p.weight}g
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* INTERACTIVE INLINE STATUS */}
                    <td data-label="State">
                      <div className="v-adm-inv-td-status">
                        <select
                          className={`v-adm-inv-badge-select ${getStatusBadgeClass(p.stockStatus)}`}
                          value={p.stockStatus}
                          onChange={(e) =>
                            handleInlineStatusChange(p, e.target.value)
                          }
                        >
                          <option value="In Stock">In Stock</option>
                          <option value="Made to Order">Made to Order</option>
                          <option value="Sold Out">Sold Out</option>
                        </select>
                        {p.stockStatus === "In Stock" && (
                          <span className="td-qty">{p.quantity} Units</span>
                        )}
                      </div>
                    </td>

                    {/* VISIBILITY */}
                    <td data-label="Visibility">
                      <button
                        className={`v-adm-inv-quick-toggle ${p.visibility === "Public" ? "pub" : "hid"}`}
                        onClick={() => handleToggleVisibility(p)}
                      >
                        {p.visibility === "Public" ? (
                          <>
                            <FaEye /> Public
                          </>
                        ) : (
                          <>
                            <FaEyeSlash /> Hidden
                          </>
                        )}
                      </button>
                    </td>

                    {/* ACTIONS */}
                    <td data-label="Actions">
                      <div className="v-adm-inv-td-actions">
                        <button
                          className="btn-icon edit"
                          title="Edit Full Specs"
                          onClick={() => openEditModal(p)}
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn-icon delete"
                          onClick={() => handleDeleteSingle(p)}
                          title="Delete"
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* FLOATING BULK COMMAND CENTER */}
      {selectedProducts.length > 0 && (
        <div className="v-adm-inv-bulk-bar pop-in-up">
          <div className="bulk-count">
            <FaCheckSquare className="bulk-icon" />
            <span>{selectedProducts.length} Items Selected</span>
          </div>

          {isBulkUpdating ? (
            <div className="bulk-loader">
              <FaCircleNotch className="spin" /> Updating Vault...
            </div>
          ) : (
            <div className="bulk-actions-scroll">
              <button
                className="bulk-btn pub"
                onClick={() => handleBulkAction("visibility", "Public")}
              >
                <FaEye /> Set Public
              </button>
              <button
                className="bulk-btn hid"
                onClick={() => handleBulkAction("visibility", "Hidden")}
              >
                <FaEyeSlash /> Set Hidden
              </button>
              <div className="bulk-divider"></div>
              <button
                className="bulk-btn stock"
                onClick={() => handleBulkAction("stockStatus", "In Stock")}
              >
                🟢 In Stock
              </button>
              <button
                className="bulk-btn mto"
                onClick={() => handleBulkAction("stockStatus", "Made to Order")}
              >
                📖 MTO
              </button>
              <button
                className="bulk-btn sold"
                onClick={() => handleBulkAction("stockStatus", "Sold Out")}
              >
                🔴 Sold Out
              </button>
              <div className="bulk-divider"></div>
              <button
                className="bulk-btn del"
                onClick={() => handleBulkAction("delete", null)}
              >
                <FaTrashAlt /> Delete
              </button>
            </div>
          )}
        </div>
      )}

      {/* FULL EDIT MODAL */}
      {editModalOpen && editForm && (
        <div className="v-adm-inv-modal-overlay">
          <div
            className="v-adm-inv-modal-backdrop"
            onClick={() => setEditModalOpen(false)}
          ></div>
          <div className="v-adm-inv-modal-card pop-in">
            <div className="modal-header">
              <h3>Edit Full Specifications</h3>
              <button
                type="button"
                className="close-btn"
                onClick={() => setEditModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            {/* Modal Body & Footer remain exactly the same as previously built, providing deep edit access */}
            <form className="modal-body" onSubmit={saveEdit}>
              <div className="form-group-row">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    name="name"
                    value={editForm.name}
                    onChange={handleEditChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>SKU</label>
                  <input
                    type="text"
                    name="sku"
                    value={editForm.sku}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              </div>
              <div className="form-group-row">
                <div className="form-group">
                  <label>Stock Status</label>
                  <select
                    name="stockStatus"
                    value={editForm.stockStatus}
                    onChange={handleEditChange}
                  >
                    <option value="In Stock">In Stock</option>
                    <option value="Made to Order">
                      Made to Order (Catalog)
                    </option>
                    <option value="Sold Out">Sold Out</option>
                  </select>
                </div>
                {editForm.stockStatus === "In Stock" && (
                  <div className="form-group">
                    <label>Quantity</label>
                    <input
                      type="number"
                      name="quantity"
                      min="0"
                      value={editForm.quantity}
                      onChange={handleEditChange}
                      required
                    />
                  </div>
                )}
              </div>
              <div className="form-group-row">
                <div className="form-group">
                  <label>Weight (g)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="weight"
                    value={editForm.weight}
                    onChange={handleEditChange}
                  />
                </div>
                <div className="form-group">
                  <label>Visibility</label>
                  <select
                    name="visibility"
                    value={editForm.visibility}
                    onChange={handleEditChange}
                  >
                    <option value="Public">Public (Retailers see this)</option>
                    <option value="Hidden">Hidden (Internal Only)</option>
                  </select>
                </div>
              </div>
              <div className="form-group media-group">
                <label>Manage Media</label>
                <div className="edit-gallery">
                  {editForm.images?.map((img, i) => (
                    <div key={`old-${i}`} className="edit-thumb">
                      <img src={img} alt="Product" />
                      <button
                        type="button"
                        onClick={() => handleRemoveExistingImage(i)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  {editForm.newFiles?.map((file, i) => (
                    <div key={`new-${i}`} className="edit-thumb new-upload">
                      <img src={URL.createObjectURL(file)} alt="New upload" />
                      <button
                        type="button"
                        onClick={() => handleRemoveNewImage(i)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                  <div
                    className="add-image-box"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <FaCamera />
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleEditImageUpload}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={isSaving}>
                  {isSaving ? (
                    <FaCircleNotch className="spin" />
                  ) : (
                    <>
                      <FaSave /> Save Product
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllProducts;
